import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Select from 'react-select';
import { FaEdit, FaCode, FaRegCopy, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { TbSettingsCancel } from 'react-icons/tb';
import { API } from '../../api/Api';
import { BeatLoader } from 'react-spinners';
import toast, { Toaster } from 'react-hot-toast';

// Endpoint names kept together so a rename stays a one-line change.
const LIST_PATH = '/visualizer/getdistributorlist';
const SAVE_PATH = '/visualizer/createupdatedistributor';
// Defaults have their own endpoint and their own column, exactly like a
// layout's do — they are not part of the settings blob.
const SAVE_DEFAULTS_PATH = '/visualizer/savedistributordefaultsettings';
const DELETE_PATH = '/visualizer/deletedistributor';

// Everything the distributor is configured with travels in the `settings` column
// as one JSON string, so this is the shape written into and read back out of it.
// Kept in one place because the API only ever sees an opaque string — if this
// drifts between save and load the mismatch is silent.
const EMPTY_SETTINGS = { collection: [], layout: {}, price: 'price', credit: true, slug: '' };

// What the visualizer shows down the right-hand side. Stored in the same
// `price` key a boolean used to live in, so nothing had to be migrated: the
// reader below still understands the old true/false.
const PANEL_MODES = [
    { value: 'price', label: 'Show Price Panel' },
    { value: 'distributor', label: 'Show Distributor Panel' },
    { value: 'none', label: 'No Side Bar' },
];

// Short form for the list column, where the full sentence would not fit.
const PANEL_MODE_SHORT = {
    price: 'Price panel',
    distributor: 'Distributor panel',
    none: 'No side bar',
};

// Rows written before the dropdown existed carry a boolean here. false meant
// hide the panel, which is exactly 'none'; anything else meant the price
// panel. An unknown string falls back to 'price' rather than blanking the
// dropdown on a value we do not recognise.
const readPanelMode = (value) => {
    if (value === false || value === 'none') return 'none';
    if (value === 'distributor') return 'distributor';
    return 'price';
};

// Whether the © Domain Cabinets credit sits on this distributor's render.
// Rows saved before the checkbox existed have no key here, and they were all
// showing it, so anything other than an explicit false reads as on — nobody's
// render changes just because the field was added.
const readShowCredit = (value) => value !== false;

// toast.error and the list banner render whatever they are handed as a React
// child, so a non-string takes the whole page down with "Objects are not
// valid as a React child" instead of showing a message. This API answers with
// its reason under any of several keys and sometimes sends an object, so take
// the first candidate that is actually text and let the caller's own wording
// stand in when none of them is.
const messageFrom = (...candidates) => {
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return '';
};

// A URL-safe short name for the distributor. Nothing reads it yet — it is
// stored now so the addresses can move from /distributor/<id> to
// /distributor/<slug> later without every distributor having to be edited
// again. Two passes so typing stays natural: `typed` runs on every keystroke
// and only drops what can never be in a slug, while `tidy` also collapses and
// trims hyphens and so would eat the one you just typed mid-word.
const typedSlug = (value) =>
    String(value || '')
        // Split the accents off their letters and drop them, so a name like
        // “Café” slugs as “cafe” rather than losing the letter entirely.
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9-]/g, '');

const tidySlug = (value) =>
    typedSlug(value).replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');

const buildSettings = ({ collectionIds, layoutAngles, panelMode, showCredit }) =>
    JSON.stringify({
        collection: collectionIds,
        // Already keyed by layout id with an array of view keys, which is exactly
        // the agreed shape — no reshaping needed.
        layout: layoutAngles,
        price: panelMode,
        credit: showCredit,
    });

const parseSettings = (raw) => {
    if (!raw) return EMPTY_SETTINGS;
    if (typeof raw === 'object') return { ...EMPTY_SETTINGS, ...raw };
    try {
        return { ...EMPTY_SETTINGS, ...JSON.parse(raw) };
    } catch {
        // A row saved before this shape existed should not break the list.
        return EMPTY_SETTINGS;
    }
};

// The same four directions and two appliance finishes the Layouts screen
// offers, and the same stored spellings, so a distributor's defaults and a
// layout's are read by identical frontend code.
const GRAIN_OPTIONS = [
    { key: 'V', direction: 'Vertical' },
    { key: 'H', direction: 'Horizontal' },
    { key: 'VS', direction: 'Vertical Sequencing' },
    { key: 'HS', direction: 'Horizontal Sequencing' },
];

const APPLIANCE_OPTIONS = ['Stainless Steel', 'Panel Ready'];

// Grain, style and appliance belong to cabinetry; a backsplash has none of them.
const CABINET_CATEGORY = 'Cabinets';

// One selection colour for the whole form — chips, selected layout cards,
// selected view thumbnails and the price toggle all use it.
const SEL = '#9ca3af';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderRadius: 8,
        borderColor: state.isFocused ? SEL : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(156,163,175,0.4)' : 'none',
        '&:hover': { borderColor: SEL },
        minHeight: 44,
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e5e7eb', borderRadius: 6 }),
    multiValueLabel: (base) => ({ ...base, color: '#374151', fontWeight: 500 }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#374151',
        ':hover': { backgroundColor: SEL, color: '#111827' },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
        color: '#1f2937',
    }),
    menu: (base) => ({ ...base, zIndex: 60 }),
};

// The slug is its own column on the distributor row, which is what the API
// takes and what the visualizer's URL carries. The settings fallback is for
// rows saved in the short window where it was kept inside the JSON blob; the
// id is the last resort so an un-slugged row still produces *some* address
// rather than /distributor/undefined.
const slugOf = (row) => tidySlug(row?.slug) || tidySlug(parseSettings(row?.settings).slug);

// The distributor's public address. Built from the browser's own origin so a
// snippet copied on staging points at staging and one copied on production
// points at production — nobody has to remember to edit the host by hand.
const embedUrlFor = (row) =>
    `${window.location.origin}/distributor/${slugOf(row) || row.id}`;

// iframe-resizer lets the embedded visualizer tell the host page how tall it
// needs to be, so the plan is never cut off inside a fixed-height frame.
const embedSnippetFor = (row) => {
    const frameId = `dc-visualizer-${slugOf(row) || row.id}`;
    return [
        `<iframe id="${frameId}" src="${embedUrlFor(row)}" width="100%" height="800" scrolling="no" frameborder="0" style="border:none;"></iframe>`,
        '',
        '<script src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.2.11/iframeResizer.min.js"></script>',
        '',
        `<script>iFrameResize({ checkOrigin: false, log: false, autoResize: true, sizeWidth: true, sizeHeight: true }, '#${frameId}');</script>`,
    ].join('\n');
};

// The list only has room for a number, and "3" on its own says nothing useful.
// Hover opens the actual names; click does the same, because a touch screen has
// no hover.
const CountWithDetails = ({ items, loading }) => {
    const [open, setOpen] = useState(false);
    if (!items.length) return <span className="text-gray-400">—</span>;
    return (
        <span
            className="relative inline-block"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium
                    hover:bg-gray-200 transition"
            >
                {items.length}
            </button>
            {open && (
                <div
                    className="absolute left-0 top-full mt-1 z-30 min-w-[220px] max-w-[340px] rounded-lg
                        border border-gray-200 bg-white shadow-lg p-3 text-xs text-gray-700 text-left"
                >
                    {loading ? (
                        <span className="text-gray-400">Loading names…</span>
                    ) : (
                        <ul className="space-y-1.5">
                            {items.map((label, i) => (
                                <li key={i} className="leading-snug">{label}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </span>
    );
};

const Section = ({ title, hint, children }) => (
    <div className="border border-gray-200 rounded-xl p-5">
        <div className="mb-3 pl-3 border-l-2 border-[#9ca3af]">
            <h3 className="font-medium text-gray-800">{title}</h3>
            {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {children}
    </div>
);

const Distributor = () => {
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_API_URL;

    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState('');
    const [distributors, setDistributors] = useState([]);

    const [optionsLoading, setOptionsLoading] = useState(true);
    const [optionsError, setOptionsError] = useState('');
    const [collections, setCollections] = useState([]);
    const [layouts, setLayouts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [colors, setColors] = useState([]);
    const [styleTypes, setStyleTypes] = useState([]);

    const [showModal, setShowModal] = useState(false);
    // The row whose embed snippet is on screen; null closes the dialog.
    const [embedRow, setEmbedRow] = useState(null);
    const [saving, setSaving] = useState(false);

    // Empty when adding. The endpoint switches on it: an id updates that row,
    // an empty one creates a new distributor.
    const [editingId, setEditingId] = useState('');
    const [name, setName] = useState('');
    const [selectedCollections, setSelectedCollections] = useState([]);
    // The ids the row was saved with. Kept separately because the options they
    // map to arrive on their own request, which can still be in flight when a
    // row is opened — see the effect below.
    const [savedCollectionIds, setSavedCollectionIds] = useState([]);
    const [layoutAngles, setLayoutAngles] = useState({});
    const [panelMode, setPanelMode] = useState('price');
    const [showCredit, setShowCredit] = useState(true);
    const [slug, setSlug] = useState('');
    // The slug follows the name on its own until someone edits it by hand; from
    // then on it is theirs and typing in Name leaves it alone. An existing row
    // counts as hand-set, because its slug is a live address — renaming the
    // distributor must not quietly repoint every embed that uses it.
    const [slugTouched, setSlugTouched] = useState(false);
    // Defaults are edited from the list, in their own dialog, the way a
    // layout's are — not inside the add/edit form. `defaultsRow` is the row
    // that dialog is open for; `defaults` holds its table, keyed by group id:
    // { [groupId]: { Texture, Grain, StyleType, Appliance } }
    const [defaultsRow, setDefaultsRow] = useState(null);
    const [defaults, setDefaults] = useState({});
    const [savingDefaults, setSavingDefaults] = useState(false);
    // The row waiting on a yes/no. Deleting is not undoable and takes the
    // distributor's URL down with it, so it never happens on a single click.
    const [deleteRow, setDeleteRow] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [status, setStatus] = useState('Active');

    /* ---------------- LIST ---------------- */
    const fetchDistributors = async () => {
        try {
            setListLoading(true);
            setListError('');
            const res = await API(`${base}${LIST_PATH}`, { status: 'Active' }, token);
            const rows = res?.Data ?? res?.Result_value;
            setDistributors(Array.isArray(rows) ? rows : []);
        } catch (err) {
            // A 400 names the field it is unhappy about, but the message can come
            // back under any of these keys — show whichever is there rather than a
            // blanket "failed", so a validation error is actionable.
            const data = err?.response?.data;
            setListError(
                messageFrom(data?.Error, data?.error, data?.Message, data?.message, err?.message) ||
                'Failed to load distributors'
            );
        } finally {
            setListLoading(false);
        }
    };

    /* ---------------- FORM OPTIONS ---------------- */
    const fetchOptions = async () => {
        try {
            setOptionsLoading(true);
            setOptionsError('');
            const masterUrl = `${base}/visualizer/getcolormasterlist`;

            // allSettled, not all: these feed different parts of the form and one
            // endpoint being unhappy must not blank the others. Sharing a single
            // Promise.all meant a failure in the colour lookups took the
            // collection and layout pickers down with them.
            const [collRes, layoutRes, groupRes, colorRes, styleRes] = await Promise.allSettled([
                API(`${base}/visualizer/getcollectionlist`, { status: 'Active' }, token),
                API(`${base}/visualizer/layouts`, { status: 'Active' }, token),
                API(masterUrl, { type: 'group' }, token),
                API(`${base}/visualizer/colors`, { status: 'Active,De-active' }, token),
                API(masterUrl, { type: 'styletype' }, token),
            ]);

            const value = (result) => (result.status === 'fulfilled' ? result.value : null);

            setCollections(value(collRes)?.Data || []);
            setGroups(value(groupRes)?.Data || []);
            setColors(value(colorRes)?.Result_value || []);
            setStyleTypes(value(styleRes)?.Data || []);

            // Say which part of the form is short rather than leaving empty
            // dropdowns that look like there is simply nothing to choose.
            const failed = [
                [collRes, 'collections'],
                [layoutRes, 'layouts'],
                [groupRes, 'groups'],
                [colorRes, 'colours'],
                [styleRes, 'style types'],
            ]
                .filter(([result]) => result.status === 'rejected')
                .map(([, label]) => label);

            if (failed.length) {
                setOptionsError(`Could not load ${failed.join(', ')}.`);
            }

            const parsed = (value(layoutRes)?.Result_value || []).map((item) => {
                let angles = [];
                try {
                    const config = JSON.parse(item.configuration || '{}');
                    if (config.angles) {
                        angles = Object.entries(config.angles).map(([key, value]) => ({
                            key,
                            name: value.name,
                            image: value.image,
                        }));
                    }
                } catch {
                   
                }
                return { ...item, angles };
            });
            setLayouts(parsed);
        } catch (err) {
            setOptionsError(err?.message || 'Could not load the form options.');
        } finally {
            setOptionsLoading(false);
        }
    };

    useEffect(() => {
        fetchDistributors();
        fetchOptions();
        // Mount-only, same as the other admin screens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------- LAYOUT / ANGLE SELECTION ---------------- */
    const toggleLayout = (layout) => {
        setLayoutAngles((prev) => {
            const next = { ...prev };
            if (next[layout.id]) {
                delete next[layout.id];
            } else {
                next[layout.id] = layout.angles.map((a) => a.key);
            }
            return next;
        });
    };

    const toggleAngle = (layoutId, angleKey) => {
        setLayoutAngles((prev) => {
            const current = prev[layoutId] || [];
            const next = current.includes(angleKey)
                ? current.filter((k) => k !== angleKey)
                : [...current, angleKey];
            return { ...prev, [layoutId]: next };
        });
    };

    // One row per category, collapsing the four cabinet groups into a single
    // "Cabinets" row the way a layout's defaults do — they share one entry, so
    // offering four separate pickers would only let them drift apart.
    const defaultRows = useMemo(() => {
        const byCategory = new Map();
        groups.forEach((g) => {
            const category = g.category || g.name;
            if (!byCategory.has(category)) {
                byCategory.set(category, { category, name: g.name, groupIds: [] });
            }
            byCategory.get(category).groupIds.push(g.id);
        });
        return [...byCategory.values()];
    }, [groups]);

    // Colours on offer for a row. Always narrowed to the categories the colour
    // says it belongs to; cabinet rows are narrowed again to the collections this
    // distributor was given, so a default can never point at a colour their own
    // customers cannot see.
    const colorsForRow = (row, allowedCollectionIds) => {
        const rowName = row.name.replace(/s$/i, '').trim().toLowerCase();
        const isCabinet = (row.category || '') === CABINET_CATEGORY;

        return colors.filter((color) => {
            const categories = (color?.category || '')
                .split(',')
                .map((c) => c.trim().replace(/s$/i, '').toLowerCase());
            if (!categories.includes(rowName)) return false;

            if (!isCabinet) return true;
            return allowedCollectionIds.includes(Number(color.collection_id));
        });
    };

    // Cabinet colours are pooled from every collection this distributor was
    // given, and a flat list of names says nothing about which is which — two
    // collections can even carry the same colour name. Grouping them under the
    // collection lets <optgroup> head each block with its name, which browsers
    // render bold. Groups follow the order collections are listed in admin,
    // not the alphabet, so this reads the same as the Collections screen.
    const colorsByCollection = (list) => {
        const byCollection = new Map();
        list.forEach((color) => {
            const id = Number(color.collection_id);
            if (!byCollection.has(id)) {
                const found = collections.find((c) => Number(c.id) === id);
                byCollection.set(id, {
                    id,
                    // Keep the id visible when the collection behind it is gone,
                    // rather than heading a block with nothing.
                    label: found?.name || `#${id}`,
                    order: found ? collections.indexOf(found) : Number.MAX_SAFE_INTEGER,
                    colors: [],
                });
            }
            byCollection.get(id).colors.push(color);
        });
        return [...byCollection.values()].sort((a, b) => a.order - b.order);
    };

    const setRowValue = (groupId, field, value) =>
        setDefaults((prev) => ({
            ...prev,
            [groupId]: { ...prev[groupId], [field]: value },
        }));

    // Stored the way a layout stores its own: one entry per category, group ids
    // joined, and the long-form grain direction rather than the short key.
    const buildDefaults = () =>
        defaultRows
            .map((row) => {
                const value = defaults[row.groupIds[0]] || {};
                if (!value.Texture) return null;
                return {
                    group_id: row.groupIds.join(','),
                    colro_id: String(value.Texture),
                    grain: GRAIN_OPTIONS.find((g) => g.key === value.Grain)?.direction || undefined,
                    Style: value.StyleType || undefined,
                    appliances: value.Appliance || undefined,
                };
            })
            .filter(Boolean);

    const readDefaults = (saved) => {
        const next = {};
        (saved || []).forEach((entry) => {
            const grainKey = GRAIN_OPTIONS.find((g) => g.direction === entry.grain)?.key || '';
            (entry.group_id || '').split(',').forEach((groupId) => {
                next[groupId] = {
                    Texture: entry.colro_id?.toString() || '',
                    Grain: grainKey,
                    StyleType: entry.Style || '',
                    Appliance: entry.appliances || '',
                };
            });
        });
        return next;
    };

    const collectionOptions = useMemo(
        () => collections.map((c) => ({ value: c.id, label: c.name })),
        [collections]
    );

    // Opening a row used to map its saved ids to options once, on the click.
    // Open one before the collection list had loaded — easy, the distributor
    // list comes back first — and the picker stayed empty for the rest of the
    // session, because nothing mapped them again once the options showed up.
    // Re-mapping here fixes that, and only when the picker is still empty, so
    // it never undoes a choice the admin has made in the open form.
    useEffect(() => {
        if (!showModal || savedCollectionIds.length === 0) return;
        setSelectedCollections((current) =>
            current.length
                ? current
                : collectionOptions.filter((o) =>
                    savedCollectionIds.includes(Number(o.value))
                )
        );
    }, [collectionOptions, showModal, savedCollectionIds]);

    // A saved row only stores ids. Turn them back into the names an admin
    // recognises, and keep the id visible when the thing behind it is gone —
    // silently dropping it would hide that the distributor points at nothing.
    const collectionNamesFor = (settings) =>
        (settings.collection || []).map((id) => {
            const found = collections.find((c) => Number(c.id) === Number(id));
            return found?.name || `#${id}`;
        });

    // Layouts carry their chosen views, so show both: the layout and what of it
    // this distributor is allowed to see.
    const layoutNamesFor = (settings) =>
        Object.entries(settings.layout || {}).map(([layoutId, views]) => {
            const layout = layouts.find((l) => String(l.id) === String(layoutId));
            const name = layout?.name || `#${layoutId}`;
            const viewNames = (views || []).map(
                (key) => layout?.angles?.find((a) => a.key === key)?.name || key
            );
            return viewNames.length ? `${name} — ${viewNames.join(', ')}` : name;
        });

    const copyEmbed = async (row) => {
        try {
            await navigator.clipboard.writeText(embedSnippetFor(row));
            toast.success('Embed code copied');
        } catch {
            // Clipboard access is refused outside a secure context, so say so
            // rather than letting the button look like it worked.
            toast.error('Could not copy — select the code and copy it manually');
        }
    };

    /* ---------------- DELETE ---------------- */
    const confirmDelete = async () => {
        try {
            setDeleting(true);
            const res = await API(
                `${base}${DELETE_PATH}`,
                { id: deleteRow.id },
                token,
                'application/json'
            );

            if (res?.Result === 1) {
                toast.success(`${deleteRow.name} deleted`);
                setDeleteRow(null);
                fetchDistributors();
            } else {
                toast.error(messageFrom(res?.Error, res?.Message) || 'Failed to delete distributor');
            }
        } catch (err) {
            const data = err?.response?.data;
            toast.error(
                messageFrom(data?.Error, data?.error, data?.Message, data?.message, err?.message) ||
                'Failed to delete distributor'
            );
        } finally {
            setDeleting(false);
        }
    };

    /* ---------------- DEFAULTS DIALOG ---------------- */
    const openDefaults = (row) => {
        // Stored as a JSON string in its own column, the same as a layout's.
        // Anything saved into settings before this endpoint existed is still
        // read, so an early distributor does not come back blank.
        let saved = row.default_settings;
        if (typeof saved === 'string') {
            try {
                saved = JSON.parse(saved || '[]');
            } catch {
                saved = [];
            }
        }
        if (!Array.isArray(saved) || !saved.length) {
            saved = parseSettings(row.settings).default || [];
        }

        setDefaults(readDefaults(saved));
        setDefaultsRow(row);
    };

    const saveDefaults = async () => {
        const payload = {
            distributor_id: defaultsRow.id,
            default_settings: JSON.stringify(buildDefaults()),
        };

        try {
            setSavingDefaults(true);
            const res = await API(`${base}${SAVE_DEFAULTS_PATH}`, payload, token, 'application/json');
            if (res?.Result === 1) {
                toast.success('Default selections saved');
                setDefaultsRow(null);
                fetchDistributors();
            } else {
                toast.error(messageFrom(res?.Error, res?.Message) || 'Failed to save default selections');
            }
        } catch (err) {
            const data = err?.response?.data;
            toast.error(
                messageFrom(data?.Error, data?.error, data?.Message, data?.message, err?.message) ||
                'Failed to save default selections'
            );
        } finally {
            setSavingDefaults(false);
        }
    };

    /* ---------------- MODAL ---------------- */
    const openAdd = () => {
        setEditingId('');
        setName('');
        setSelectedCollections([]);
        setLayoutAngles({});
        setPanelMode('price');
        setShowCredit(true);
        setSlug('');
        setSlugTouched(false);
        setSavedCollectionIds([]);
        setStatus('Active');
        setShowModal(true);
    };

    const openEdit = (row) => {
        const settings = parseSettings(row.settings);

        setEditingId(row.id);
        setName(row.name || '');
        setStatus(row.status || 'Active');
        setPanelMode(readPanelMode(settings.price));
        setShowCredit(readShowCredit(settings.credit));
        setSlug(slugOf(row));
        setSlugTouched(Boolean(slugOf(row)));

        // Match saved collection ids back to the current option list, so a
        // collection deleted since the save simply drops out instead of showing
        // as a blank chip.
        const savedIds = (settings.collection || []).map(Number);
        setSavedCollectionIds(savedIds);
        setSelectedCollections(
            collectionOptions.filter((o) => savedIds.includes(Number(o.value)))
        );

        setLayoutAngles(settings.layout || {});

        setShowModal(true);
    };

    /* ---------------- SUBMIT ---------------- */
    const handleSubmit = async () => {
        if (!name.trim()) return toast.error('Enter a distributor name');
        if (selectedCollections.length === 0) return toast.error('Choose at least one collection');
        if (Object.keys(layoutAngles).length === 0) return toast.error('Choose at least one layout');

        const emptyAngles = Object.entries(layoutAngles)
            .filter(([, angles]) => angles.length === 0)
            .map(([id]) => layouts.find((l) => String(l.id) === id)?.name || id);
        if (emptyAngles.length) {
            return toast.error(`Choose at least one view for: ${emptyAngles.join(', ')}`);
        }

        // The slug is optional, but a typed one that survives as nothing — say
        // "!!!" — would save silently as blank, so say so instead.
        const cleanSlug = tidySlug(slug);
        if (slug.trim() && !cleanSlug) {
            return toast.error('Slug needs at least one letter or number');
        }
        // Whatever ends up pointing at these, two distributors cannot share one.
        const clash = cleanSlug &&
            distributors.find((d) => d.id !== editingId && slugOf(d) === cleanSlug);
        if (clash) {
            return toast.error(`Slug “${cleanSlug}” is already used by ${clash.name}`);
        }

        const payload = {
            // Empty id creates, a real id updates that row.
            id: editingId || '',
            name: name.trim(),
            settings: buildSettings({
                collectionIds: selectedCollections.map((o) => Number(o.value)),
                layoutAngles,
                panelMode,
                showCredit,
            }),
            // Its own parameter, beside name and status — the API takes it
            // there, and it is what /distributor/<slug> is looked up by, so it
            // does not belong inside the opaque settings string.
            slug: cleanSlug,
            status,
        };

        try {
            setSaving(true);
            const res = await API(`${base}${SAVE_PATH}`, payload, token, 'application/json');
            if (res?.Result === 1) {
                toast.success('Distributor saved');
                setShowModal(false);
                fetchDistributors();
            } else {
                toast.error(messageFrom(res?.Error, res?.Message) || 'Failed to save distributor');
            }
        } catch (err) {
            const data = err?.response?.data;
            toast.error(
                messageFrom(data?.Error, data?.error, data?.Message, data?.message, err?.message) ||
                'Failed to save distributor'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Toaster position="top-center" />

            <div className="flex h-screen fixed w-full bg-gray-100">
                <div className="md:w-64 w-16 bg-gray-800">
                    <Sidebar />
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="bg-white rounded-xl shadow-md pb-4">

                        <div className="flex justify-between items-center p-4">
                            <h2 className="text-xl font-semibold text-teal-600">Distributor</h2>
                            <button
                                className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition"
                                onClick={openAdd}
                            >
                                Add Distributor
                            </button>
                        </div>

                        <div className="px-4">
                            {listLoading && (
                                <div className="flex justify-center py-12">
                                    <BeatLoader color="#14b8a6" />
                                </div>
                            )}

                            {!listLoading && listError && (
                                <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">{listError}</div>
                            )}

                            {!listLoading && !listError && (
                                <table className="w-full border">
                                    <thead className="bg-blue-100">
                                        <tr>
                                            <th className="p-3 text-left">Name</th>
                                            <th className="p-3 text-left">Collections</th>
                                            <th className="p-3 text-left">Layouts</th>
                                            <th className="p-3 text-left">Side bar</th>
                                            <th className="p-3 text-left">Copyright</th>
                                            <th className="p-3 text-left">Status</th>
                                            <th className="p-3 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {distributors.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center p-6 text-gray-500">
                                                    No distributors found
                                                </td>
                                            </tr>
                                        ) : (
                                            distributors.map((row) => {
                                                // The counts live inside the settings JSON, not as columns.
                                                const s = parseSettings(row.settings);
                                                return (
                                                    <tr key={row.id} className="border-b hover:bg-gray-50">
                                                        <td className="p-3">
                                                            {/* The name opens this distributor's own visualizer,
                                                                the same address the embed snippet points at. New
                                                                tab, so the list is not lost. A row with no slug
                                                                yet has nowhere to go, so it stays plain text
                                                                rather than becoming a link that 404s. */}
                                                            {slugOf(row) ? (
                                                                <a
                                                                    href={embedUrlFor(row)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:underline"
                                                                    title={embedUrlFor(row)}
                                                                >
                                                                    {row.name}
                                                                </a>
                                                            ) : (
                                                                row.name
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-gray-600">
                                                            <CountWithDetails
                                                                items={collectionNamesFor(s)}
                                                                loading={optionsLoading}
                                                            />
                                                        </td>
                                                        <td className="p-3 text-gray-600">
                                                            <CountWithDetails
                                                                items={layoutNamesFor(s)}
                                                                loading={optionsLoading}
                                                            />
                                                        </td>
                                                        <td className="p-3">
                                                            {/* Three states now, so a checkbox can no longer say which
                                                                one is set — the label does. Read-only either way:
                                                                editing stays in the form. */}
                                                            <span className="text-gray-600">
                                                                {PANEL_MODE_SHORT[readPanelMode(s.price)]}
                                                            </span>
                                                        </td>
                                                        <td className="p-3">
                                                            {/* Read-only, like the side bar beside it — the
                                                                checkbox that sets it lives in the form. The
                                                                title is what a screen reader and a hover get,
                                                                since the mark alone carries the meaning. */}
                                                            {readShowCredit(s.credit) ? (
                                                                <FaCheck
                                                                    className="text-green-600"
                                                                    title="Copyright shown on the render"
                                                                />
                                                            ) : (
                                                                <FaTimes
                                                                    className="text-red-600"
                                                                    title="Copyright hidden on the render"
                                                                />
                                                            )}
                                                        </td>
                                                        <td className="p-3">
                                                            <span
                                                                className={`px-2 py-1 rounded text-xs ${row.status === 'Active'
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : 'bg-gray-200 text-gray-600'
                                                                    }`}
                                                            >
                                                                {row.status || '—'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3">
                                                            <div className="flex items-center gap-4">
                                                                <button
                                                                    type="button"
                                                                    title="Default selections"
                                                                    onClick={() => openDefaults(row)}
                                                                    className="text-gray-500 hover:text-gray-800 transition"
                                                                >
                                                                    <TbSettingsCancel size={20} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    title="Embed code"
                                                                    onClick={() => setEmbedRow(row)}
                                                                    className="text-gray-500 hover:text-gray-800 transition"
                                                                >
                                                                    <FaCode />
                                                                </button>
                                                                <FaEdit
                                                                    title="Edit"
                                                                    className="text-blue-500 cursor-pointer"
                                                                    onClick={() => openEdit(row)}
                                                                />
                                                                <FaTrash
                                                                    title="Delete"
                                                                    className="text-red-500 cursor-pointer"
                                                                    onClick={() => setDeleteRow(row)}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ---------------- ADD MODAL ---------------- */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

                        <div className="flex justify-between items-center px-5 py-4 border-b">
                            <h2 className="text-lg font-semibold text-teal-600">
                                {editingId ? 'Edit Distributor' : 'Add Distributor'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-5 space-y-5 overflow-y-auto">
                            {optionsLoading ? (
                                <div className="flex justify-center py-10">
                                    <BeatLoader color="#14b8a6" />
                                </div>
                            ) : (
                                <>
                                    {optionsError && (
                                        <div className="p-3 rounded bg-red-50 text-red-700 text-sm">
                                            {optionsError} Some choices below may be missing.
                                        </div>
                                    )}

                                    <Section
                                        title="Distributor"
                                        hint="The name this distributor is identified by."
                                    >
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                if (!slugTouched) setSlug(tidySlug(e.target.value));
                                            }}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#9ca3af] focus:border-[#9ca3af] transition"
                                            placeholder="e.g. Domain Cabinets"
                                        />

                                        <label className="block text-sm text-gray-700 mt-4 mb-1">
                                            Slug
                                        </label>
                                        <input
                                            type="text"
                                            value={slug}
                                            onChange={(e) => {
                                                setSlugTouched(true);
                                                setSlug(typedSlug(e.target.value));
                                            }}
                                            onBlur={() => setSlug(tidySlug(slug))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#9ca3af] focus:border-[#9ca3af] transition"
                                            placeholder="e.g. domain-cabinets"
                                        />
                                        
                                    </Section>

                                    <Section
                                        title="Collections"
                                        hint="Only these collections appear in the distributor's visualizer."
                                    >
                                        <Select
                                            isMulti
                                            options={collectionOptions}
                                            value={selectedCollections}
                                            onChange={(v) => setSelectedCollections(v || [])}
                                            placeholder="Choose collections…"
                                            styles={selectStyles}
                                        />
                                    </Section>

                                    <Section
                                        title="Layouts & views"
                                        hint="Pick a layout, then trim its views. Each layout offers its own."
                                    >
                                        <div className="space-y-2">
                                            {layouts.map((layout) => {
                                                const chosenAngles = layoutAngles[layout.id];
                                                const chosen = !!chosenAngles;
                                                return (
                                                    <div
                                                        key={layout.id}
                                                        className={`rounded-lg border transition ${chosen
                                                            ? 'border-[#9ca3af] bg-[#9ca3af]/10'
                                                            : 'border-gray-200 hover:border-gray-300 bg-white'
                                                            }`}
                                                    >
                                                        <label className="flex items-center gap-3 p-3 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={chosen}
                                                                onChange={() => toggleLayout(layout)}
                                                                className="w-4 h-4 accent-[#9ca3af]"
                                                            />
                                                            <span className="font-medium text-gray-800 flex-1">
                                                                {layout.name}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                {chosen
                                                                    ? `${chosenAngles.length} of ${layout.angles.length} views`
                                                                    : `${layout.angles.length} views`}
                                                            </span>
                                                        </label>

                                                        {chosen && (
                                                            <div className="px-3 pb-3 pl-10 flex flex-wrap gap-3">
                                                                {layout.angles.map((a) => {
                                                                    const on = chosenAngles.includes(a.key);
                                                                    return (
                                                                        <button
                                                                            key={a.key}
                                                                            type="button"
                                                                            onClick={() => toggleAngle(layout.id, a.key)}
                                                                            title={a.name || a.key}
                                                                            className={`relative w-24 rounded-lg overflow-hidden border-2 transition ${on
                                                                                ? 'border-[#9ca3af] shadow-sm'
                                                                                : 'border-gray-200 opacity-60 hover:opacity-100 hover:border-gray-300'
                                                                                }`}
                                                                        >
                                                                            {a.image ? (
                                                                                <img
                                                                                    src={`${process.env.REACT_APP_VISUAL_APP_IMAGE_BASE_URL || ''}${a.image}`}
                                                                                    alt={a.name || a.key}
                                                                                    className="w-24 h-14 object-cover bg-gray-100"
                                                                                />
                                                                            ) : (
                                                                                <div className="w-24 h-14 bg-gray-100" />
                                                                            )}

                                                                            {on && (
                                                                                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#9ca3af] text-white text-[10px] flex items-center justify-center leading-none">
                                                                                    ✓
                                                                                </span>
                                                                            )}

                                                                            <span
                                                                                className={`block text-[11px] py-1 truncate px-1 ${on ? 'bg-[#9ca3af] text-white' : 'bg-gray-100 text-gray-600'
                                                                                    }`}
                                                                            >
                                                                                {a.name || a.key}
                                                                            </span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </Section>

                                    <Section
                                        title="Price display"
                                        hint="What this distributor's customers see down the right-hand side."
                                    >
                                        <select
                                            value={panelMode}
                                            onChange={(e) => setPanelMode(e.target.value)}
                                            className="border rounded px-3 py-2 text-sm text-gray-700 w-full max-w-xs
                                                focus:outline-none focus:ring-2 focus:ring-teal-400"
                                        >
                                            {PANEL_MODES.map((mode) => (
                                                <option key={mode.value} value={mode.value}>
                                                    {mode.label}
                                                </option>
                                            ))}
                                        </select>
                                        {panelMode === 'none' && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                The reopen tab goes with it — no “$” is left on the render.
                                            </p>
                                        )}
                                    </Section>

                                    <Section
                                        title="Copyright credit"
                                        hint="The “© Domain Cabinets” line in the bottom-left corner of the render."
                                    >
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={showCredit}
                                                onChange={(e) => setShowCredit(e.target.checked)}
                                                className="w-4 h-4 accent-[#9ca3af]"
                                            />
                                            <span className="text-sm text-gray-700">
                                                Show the copyright credit on the render
                                            </span>
                                        </label>
                                    </Section>

                                    <Section
                                        title="Status"
                                        hint="A de-activated distributor's URL stops serving."
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setStatus((s) => (s === 'Active' ? 'De-active' : 'Active'))
                                            }
                                            className="flex items-center gap-3"
                                        >
                                            <span
                                                className={`relative w-14 h-7 flex items-center rounded-full transition-colors ${status === 'Active' ? 'bg-[#9ca3af]' : 'bg-gray-300'
                                                    }`}
                                            >
                                                <span
                                                    className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${status === 'Active' ? 'translate-x-7' : 'translate-x-0'
                                                        }`}
                                                />
                                            </span>
                                            <span className="text-gray-700 text-sm">{status}</span>
                                        </button>
                                    </Section>
                                </>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 px-5 py-4 border-t">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-5 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving || optionsLoading}
                                className="bg-gradient-to-r from-teal-400 to-teal-500 text-white px-8 py-2 rounded shadow
                                    hover:from-teal-500 hover:to-teal-600
                                    disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? 'Saving...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- DELETE CONFIRM ---------------- */}
            {deleteRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h3 className="font-semibold text-gray-800 mb-2">Delete distributor</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            <span className="font-medium">{deleteRow.name}</span> will be removed
                            along with its collections, layouts and default selections. Any page
                            embedding its visualizer will stop working. This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteRow(null)}
                                className="px-5 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="px-5 py-2 rounded bg-red-500 text-white hover:bg-red-600
                                    disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- DEFAULT SELECTIONS ---------------- */}
            {defaultsRow && (() => {
                // Cabinet colours come from the collections THIS distributor was
                // given, so a default can never point at something their own
                // customers cannot see.
                const allowedCollectionIds = (parseSettings(defaultsRow.settings).collection || [])
                    .map(Number);

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                            <div className="flex justify-between items-center px-5 py-4 border-b">
                                <div>
                                    <h3 className="font-semibold text-gray-800">
                                        Default selections — {defaultsRow.name}
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        What this distributor's visualizer opens on. Leave a row
                                        blank to fall back to the layout's own default.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setDefaultsRow(null)}
                                    className="w-8 h-8 rounded-full bg-red-500 text-white hover:bg-red-600"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-5 overflow-y-auto">
                                {allowedCollectionIds.length === 0 ? (
                                    <p className="text-sm text-gray-500">
                                        This distributor has no collections yet. Add some from Edit
                                        first — cabinet colours are offered from those.
                                    </p>
                                ) : (
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left font-medium text-gray-700">Group</th>
                                                            <th className="px-4 py-2 text-left font-medium text-gray-700">Color</th>
                                                            <th className="px-4 py-2 text-left font-medium text-gray-700">Grain</th>
                                                            <th className="px-4 py-2 text-left font-medium text-gray-700">Appliance</th>
                                                            <th className="px-4 py-2 text-left font-medium text-gray-700">Style Type</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {defaultRows.map((row) => {
                                                            // One picker drives the whole category, so read and
                                                            // write through its first group id.
                                                            const key = row.groupIds[0];
                                                            const value = defaults[key] || {};
                                                            const isCabinet = (row.category || '') === CABINET_CATEGORY;
                                                            const options = colorsForRow(row, allowedCollectionIds);

                                                            return (
                                                                <tr key={key} className="border-b">
                                                                    <td className="px-4 py-3 font-medium text-gray-800">
                                                                        {row.category || row.name}
                                                                    </td>

                                                                    <td className="px-4 py-3">
                                                                        <select
                                                                            className="border border-gray-300 rounded px-2 py-1 max-w-[190px]"
                                                                            value={value.Texture?.toString() || ''}
                                                                            onChange={(e) => setRowValue(key, 'Texture', e.target.value)}
                                                                        >
                                                                            <option value="">Select color</option>
                                                                            {isCabinet
                                                                                ? colorsByCollection(options).map((group) => (
                                                                                    <optgroup key={group.id} label={group.label}>
                                                                                        {group.colors.map((color) => (
                                                                                            <option key={color.id} value={color.id.toString()}>
                                                                                                {color.name}
                                                                                            </option>
                                                                                        ))}
                                                                                    </optgroup>
                                                                                ))
                                                                                : options.map((color) => (
                                                                                    <option key={color.id} value={color.id.toString()}>
                                                                                        {color.name}
                                                                                    </option>
                                                                                ))}
                                                                        </select>
                                                                        {options.length === 0 && (
                                                                            <p className="text-xs text-gray-400 mt-1">
                                                                                No colours available for this group.
                                                                            </p>
                                                                        )}
                                                                    </td>

                                                                    <td className="px-4 py-3">
                                                                        {isCabinet ? (
                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                {GRAIN_OPTIONS.map((grain) => (
                                                                                    <button
                                                                                        key={grain.key}
                                                                                        type="button"
                                                                                        onClick={() => setRowValue(key, 'Grain', grain.key)}
                                                                                        className={`px-2 py-1 border rounded text-xs transition ${value.Grain === grain.key
                                                                                            ? 'bg-[#9ca3af] text-white border-[#9ca3af]'
                                                                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                                            }`}
                                                                                    >
                                                                                        {grain.direction}
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-gray-300">—</span>
                                                                        )}
                                                                    </td>

                                                                    <td className="px-4 py-3">
                                                                        {isCabinet ? (
                                                                            <select
                                                                                className="border border-gray-300 rounded px-2 py-1"
                                                                                value={value.Appliance || ''}
                                                                                onChange={(e) => setRowValue(key, 'Appliance', e.target.value)}
                                                                            >
                                                                                <option value="">Select</option>
                                                                                {APPLIANCE_OPTIONS.map((a) => (
                                                                                    <option key={a} value={a}>{a}</option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            <span className="text-gray-300">—</span>
                                                                        )}
                                                                    </td>

                                                                    <td className="px-4 py-3">
                                                                        {isCabinet ? (
                                                                            <select
                                                                                className="border border-gray-300 rounded px-2 py-1"
                                                                                value={value.StyleType || ''}
                                                                                onChange={(e) => setRowValue(key, 'StyleType', e.target.value)}
                                                                            >
                                                                                <option value="">Select</option>
                                                                                {styleTypes.map((st) => (
                                                                                    <option key={st.id} value={st.id}>{st.name}</option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            <span className="text-gray-300">—</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                )}
                            </div>

                            <div className="flex justify-end gap-3 px-5 py-4 border-t">
                                <button
                                    onClick={() => setDefaultsRow(null)}
                                    className="px-5 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveDefaults}
                                    disabled={savingDefaults}
                                    className="bg-gradient-to-r from-teal-400 to-teal-500 text-white px-8 py-2 rounded shadow
                                        hover:from-teal-500 hover:to-teal-600
                                        disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {savingDefaults ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ---------------- EMBED CODE ---------------- */}
            {embedRow && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
                        <div className="flex justify-between items-center px-5 py-4 border-b">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <FaCode className="text-gray-500" />
                                Embed code — {embedRow.name}
                            </h3>
                            <button
                                onClick={() => setEmbedRow(null)}
                                className="w-8 h-8 rounded-full bg-red-500 text-white hover:bg-red-600"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="p-5">
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-xs text-gray-500">
                                    Paste this into the page where the visualizer should appear.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => copyEmbed(embedRow)}
                                    className="flex items-center gap-2 text-sm px-3 py-1.5 rounded border
                                        border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                                >
                                    <FaRegCopy /> Copy code
                                </button>
                            </div>

                            <pre
                                className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto
                                    whitespace-pre-wrap break-all leading-relaxed select-all"
                            >
                                {embedSnippetFor(embedRow)}
                            </pre>
                        </div>

                        <div className="flex justify-end px-5 py-4 border-t">
                            <button
                                onClick={() => setEmbedRow(null)}
                                className="px-5 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Distributor;
